import { useCallback, useEffect, useState } from 'react';
import { useEdges, useFlowView } from '@/components/Recipe/FlowContext';
import { getUnconnectedIterators, hasEmptyIterator } from '@/utils/iterator.utils';
import type { PrecedingIteratorData } from '@/utils/flow';
import type { NodeId } from 'web';

/**
 * Empty-iterator validation for running one node alone. An unconnected empty iterator disables Run up front. A
 * connected one may be filled by upstream nodes, so it only blocks Run once Run is pressed; that warning clears when
 * values arrive or a run starts.
 */
export const useEmptyIteratorValidation = ({
  nodeId,
  precedingIterators,
  isRunActive,
}: {
  /** The node run alone, or null to turn the check off. */
  nodeId: NodeId | null;
  precedingIterators: PrecedingIteratorData[] | null;
  isRunActive: boolean;
}) => {
  const flowView = useFlowView();
  const edges = useEdges();
  const [blockedRunNodeId, setBlockedRunNodeId] = useState<NodeId | null>(null);

  useEffect(() => {
    if (isRunActive) setBlockedRunNodeId(null);
  }, [isRunActive]);

  const listSelectorOptions = (iteratorId: NodeId) => flowView.listSelectorOptions(iteratorId);
  const isAnyIteratorEmpty = !!nodeId && hasEmptyIterator(precedingIterators, listSelectorOptions);
  const isRunDisabled =
    !!nodeId && hasEmptyIterator(getUnconnectedIterators(precedingIterators, edges), listSelectorOptions);

  /** Call from a Run handler. Returns true when the run must not start. */
  const blockRunIfEmpty = useCallback(() => {
    setBlockedRunNodeId(isAnyIteratorEmpty ? nodeId : null);
    return isAnyIteratorEmpty;
  }, [isAnyIteratorEmpty, nodeId]);

  return {
    isRunDisabled,
    showWarning: isRunDisabled || (isAnyIteratorEmpty && blockedRunNodeId === nodeId),
    blockRunIfEmpty,
  };
};
